// BLD-28 /planos/:planId/diagnostico: the maturity categories a workshop member answers (review V2 point 1) or, as a
// group manager, validates (point 3). No scores or panels here: those stay with the consultant.
import { useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardCheck, MessageSquareText } from "lucide-react";
import { Alert, Button, EmptyState, ErrorState, PageHeader, Panel, Pill, Skeleton, TextArea } from "../components/ui.tsx";
import { Modal } from "../components/dialog.tsx";
import { BackLink, StickyActions } from "../components/nav-controls.tsx";
import { GuardedLink } from "../components/plan-shell.tsx";
import { useToast } from "../components/toast.tsx";
import { AnswerOptions } from "../components/maturity/answer-options.tsx";
import { ApiError, get, post, put } from "../lib/api.ts";
import { usePlan } from "../lib/plan.tsx";
import { usePageTitle } from "../lib/nav.tsx";
import { useUnsavedGuard } from "../lib/guard.tsx";
import {
  PROGNOSIS_NOTE, SCENARIO_LABEL, SCENARIO_TAGLINE, useWorkshopDiagnosis, workshopKey,
  type ScenarioKind, type WorkshopCategoryDetail, type WorkshopCategoryItem,
} from "../lib/maturity.ts";
import { formatDateBR } from "../../shared/dates.ts";
import { MATURITY_LIMITS } from "../../shared/maturity/definition.ts";
import type { Answer } from "../../shared/maturity/responses.ts";
import { ANSWER_LABEL, NO_ANSWER_LABEL } from "../../shared/maturity/scoring.ts";
import { CATEGORY_STATE_LABEL, CATEGORY_STATE_TONE, NOTE_MAX } from "../../shared/maturity/workflow.ts";

const categoryHref = (planId: string, assessmentId: string, kind: ScenarioKind, categoryId: string) =>
  `/planos/${planId}/diagnostico/${assessmentId}/${kind}/${categoryId}`;

function actionLabel(c: WorkshopCategoryItem) {
  if (c.capabilities.can_edit) return c.answered ? "Continuar" : "Responder";
  if (c.capabilities.can_validate) return "Validar";
  return "Ver";
}

export function WorkshopDiagnosisPage() {
  const plan = usePlan();
  usePageTitle("Diagnóstico", plan.name);
  const q = useWorkshopDiagnosis(plan.id);
  return (
    <>
      <PageHeader title="Diagnóstico institucional" subtitle="Categorias do diagnóstico de maturidade que você responde ou valida nesta oficina." />
      {q.isPending ? <Skeleton lines={6} /> : q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : q.data.length === 0 ? (
        <EmptyState title="Nenhuma categoria atribuída a você.">Quando o consultor atribuir categorias do diagnóstico a você ou ao grupo que você gerencia, elas aparecem aqui.</EmptyState>
      ) : (
        <div className="space-y-5">
          {q.data.map((a) => (
            <Panel key={a.id} as="section">
              <h2 className="text-lg font-bold">{a.name}</h2>
              <p className="text-sm text-muted">Referência <span className="num">{formatDateBR(a.reference_date)}</span></p>
              {a.scenarios.map((s) => (
                <div key={s.kind} className="mt-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-[15px] font-semibold">{SCENARIO_LABEL[s.kind]} <span className="font-normal text-muted">· {SCENARIO_TAGLINE[s.kind]}</span></h3>
                    {s.state === "finalized" && <Pill tone="green">Encerrado pelo consultor</Pill>}
                  </div>
                  <ul className="mt-2 divide-y divide-line rounded-ctl border border-line">
                    {s.categories.map((c) => (
                      <li key={c.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-3">
                        <div className="min-w-0 flex-1 basis-64">
                          <p className="text-sm leading-snug font-semibold">{c.title}</p>
                          <p className="mt-0.5 text-xs text-muted">
                            {c.roles.answerer ? (c.roles.manager ? "Você responde e valida (gestor)" : "Você responde") : `Você valida as respostas de ${c.answerer_name}`} · {c.group_name} · <span className="num">{c.answered} de {c.total}</span> respondidos
                          </p>
                          {c.state === "returned" && c.note && <p className="mt-1 text-xs text-[#76511b]">Devolvida: {c.note}</p>}
                        </div>
                        {s.state === "draft" && <Pill tone={CATEGORY_STATE_TONE[c.state]}>{CATEGORY_STATE_LABEL[c.state]}</Pill>}
                        <GuardedLink href={categoryHref(plan.id, a.id, s.kind, c.id)} aria-label={`${actionLabel(c)}: ${c.title}`}
                          className={c.capabilities.can_edit || c.capabilities.can_validate
                            ? "inline-flex min-h-10 items-center rounded-ctl bg-primary px-4 text-sm font-semibold text-white shadow-e1 hover:bg-primary-dark"
                            : "elevate inline-flex min-h-10 items-center rounded-ctl px-3 text-sm font-semibold text-primary"}>
                          {actionLabel(c)}
                        </GuardedLink>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </Panel>
          ))}
        </div>
      )}
    </>
  );
}

type Buffer = Record<string, { answer: Answer | null; group_comment: string | null }>;
const toBuffer = (d: WorkshopCategoryDetail): Buffer =>
  Object.fromEntries(d.category.questions.map((q) => [q.id, { answer: d.entries[q.id]?.answer ?? null, group_comment: d.entries[q.id]?.group_comment ?? null }]));

export function WorkshopCategoryPage({ assessmentId, kind, categoryId }: { assessmentId: string; kind: ScenarioKind; categoryId: string }) {
  const plan = usePlan();
  const path = `/plans/${plan.id}/diagnostico/assessments/${assessmentId}/${kind}/categories/${categoryId}`;
  const q = useQuery({ queryKey: workshopKey(plan.id, assessmentId, kind, categoryId), queryFn: async () => (await get<{ data: WorkshopCategoryDetail }>(path)).data });
  usePageTitle(q.data?.category.title ?? "Diagnóstico", plan.name);
  const back = <BackLink fallback={`/planos/${plan.id}/diagnostico`} label="Diagnóstico" />;
  if (q.isPending) return <><div className="mb-2">{back}</div><Skeleton lines={8} /></>;
  if (q.isError) return <><div className="mb-2">{back}</div><ErrorState error={q.error} onRetry={() => void q.refetch()} /></>;
  return <CategoryForm key={`${q.data.version}:${q.data.state}`} detail={q.data} path={path} back={back} />;
}

function CategoryForm({ detail: d, path, back }: { detail: WorkshopCategoryDetail; path: string; back: ReactNode }) {
  const plan = usePlan();
  const qc = useQueryClient();
  const toast = useToast();
  const [buffer, setBuffer] = useState<Buffer>(() => toBuffer(d));
  const [busy, setBusy] = useState(false);
  const [returning, setReturning] = useState(false);
  const [note, setNote] = useState("");
  const [conflict, setConflict] = useState(false);
  const caps = d.capabilities;
  const dirty = caps.can_edit && JSON.stringify(buffer) !== JSON.stringify(toBuffer(d));
  useUnsavedGuard(dirty);
  const answered = d.category.questions.filter((x) => buffer[x.id]?.answer).length;
  const total = d.category.questions.length;

  async function apply(next: WorkshopCategoryDetail, message: string) {
    qc.setQueryData(workshopKey(plan.id, d.assessment.id, d.kind, d.category.id), next);
    await qc.invalidateQueries({ queryKey: workshopKey(plan.id), exact: true });
    toast("success", message);
  }
  function fail(e: unknown) {
    if (e instanceof ApiError && e.code === "VERSION_CONFLICT") setConflict(true);
    else toast("danger", e instanceof Error ? e.message : "Não foi possível concluir.");
  }
  async function save(): Promise<WorkshopCategoryDetail | null> {
    const res = await put<{ data: WorkshopCategoryDetail }>(path, { expected_version: d.version, responses: buffer });
    return res.data;
  }
  async function run(fn: () => Promise<void>) {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }
  const onSave = () => void run(async () => { const next = await save(); if (next) await apply(next, "Respostas salvas."); });
  const onSubmit = () => void run(async () => {
    const version = dirty ? (await save())!.version : d.version;
    const res = await post<{ data: WorkshopCategoryDetail }>(`${path}/submit`, { expected_version: version });
    await apply(res.data, d.roles.manager ? "Enviada. Como gestor do grupo, valide em seguida." : "Enviada para validação do gestor do grupo.");
  });
  const onValidate = () => void run(async () => {
    const res = await post<{ data: WorkshopCategoryDetail }>(`${path}/validate`, { expected_version: d.version });
    await apply(res.data, "Categoria validada. O consultor fará a aprovação final.");
  });
  const onReturn = () => void run(async () => {
    const res = await post<{ data: WorkshopCategoryDetail }>(`${path}/return`, { expected_version: d.version, note: note.trim() });
    setReturning(false);
    await apply(res.data, "Categoria devolvida ao avaliador.");
  });
  const frozen = !caps.can_edit;

  return (
    <div>
      <div className="mb-2">{back}</div>
      <PageHeader eyebrow={`${d.assessment.name} · ${SCENARIO_LABEL[d.kind]} (${SCENARIO_TAGLINE[d.kind].toLowerCase()})`} title={d.category.title}
        subtitle={<>{d.subdimension.title} · Grupo {d.group_name} · {d.roles.answerer ? "você responde" : `responde ${d.answerer_name}`}</>} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Pill tone={CATEGORY_STATE_TONE[d.state]}>{CATEGORY_STATE_LABEL[d.state]}</Pill>
        <span className="num text-sm text-muted" aria-live="polite">{answered} de {total} quesitos respondidos</span>
      </div>
      {d.kind === "prognosis" && <p className="mb-3 text-sm text-muted">{PROGNOSIS_NOTE} Responda pensando em como a instituição poderá ficar.</p>}
      {d.scenario_state === "finalized" && <Alert tone="success" title="Encerrado pelo consultor.">As respostas estão preservadas e não podem mais ser alteradas.</Alert>}
      {d.state === "returned" && d.note && <Alert tone="warn" title="Devolvida para ajuste">{d.note}</Alert>}
      {d.state === "submitted" && d.roles.answerer && !d.roles.manager && <Alert tone="info">Enviada para validação do gestor do grupo. Você poderá editar de novo se ela for devolvida.</Alert>}
      {d.state === "validated" && <Alert tone="info">Validada pelo gestor. Aguardando a aprovação do consultor.</Alert>}
      {d.state === "approved" && <Alert tone="success">Aprovada pelo consultor.</Alert>}
      {conflict && (
        <Alert tone="warn" title="Esta categoria foi alterada por outra pessoa."
          action={<Button variant="secondary" onClick={() => { setConflict(false); void qc.invalidateQueries({ queryKey: workshopKey(plan.id, d.assessment.id, d.kind, d.category.id) }); }}>Carregar versão atual</Button>}>
          Suas alterações não foram salvas.
        </Alert>
      )}
      <ol className="space-y-3">
        {d.category.questions.map((x) => {
          const e = buffer[x.id]!;
          return (
            <li key={x.id} className="rounded-panel border border-line bg-surface p-4 shadow-e1">
              <fieldset>
                <legend className="w-full">
                  <span className="eyebrow block">{x.code}</span>
                  <span className="mt-0.5 block text-[15px] leading-snug">{x.text}</span>
                </legend>
                {d.reference && <p className="mt-2 text-xs text-muted">Diagnóstico: <span className="font-semibold text-ink">{d.reference[x.id] ? ANSWER_LABEL[d.reference[x.id]!] : NO_ANSWER_LABEL}</span></p>}
                <AnswerOptions name={`answer-${x.id}`} value={e.answer} frozen={frozen} lockedLabel={CATEGORY_STATE_LABEL[d.state]}
                  onChange={(v) => setBuffer((b) => ({ ...b, [x.id]: { ...b[x.id]!, answer: v } }))} />
              </fieldset>
              {frozen ? (
                e.group_comment && <p className="mt-3 rounded-ctl bg-paper p-3 text-sm whitespace-pre-wrap"><span className="font-semibold">Comentário do grupo: </span>{e.group_comment}</p>
              ) : (
                <details className="mt-3" open={Boolean(e.group_comment)}>
                  <summary className="elevate inline-flex min-h-9 cursor-pointer items-center gap-1.5 rounded-ctl px-2 text-sm font-semibold text-primary">
                    <MessageSquareText className="size-4" aria-hidden />Comentário do grupo (opcional)
                  </summary>
                  <TextArea label="Comentário do grupo" maxLength={MATURITY_LIMITS.comment} value={e.group_comment ?? ""}
                    onChange={(ev) => setBuffer((b) => ({ ...b, [x.id]: { ...b[x.id]!, group_comment: ev.target.value || null } }))} />
                </details>
              )}
            </li>
          );
        })}
      </ol>
      {(caps.can_edit || caps.can_validate) && (
        <StickyActions dirty={dirty} onSave={caps.can_edit ? onSave : undefined} summary={<span className="num text-muted">{answered} de {total} respondidos</span>}>
          {caps.can_edit && <Button variant="secondary" disabled={!dirty || busy} loading={busy && dirty} onClick={onSave}>Salvar</Button>}
          {caps.can_submit && <Button disabled={busy || answered < total} onClick={onSubmit} title={answered < total ? "Responda todos os quesitos para enviar" : undefined}><ClipboardCheck className="size-4" aria-hidden />Enviar para validação</Button>}
          {caps.can_validate && <Button variant="secondary" disabled={busy} onClick={() => setReturning(true)}>Devolver</Button>}
          {caps.can_validate && <Button loading={busy} onClick={onValidate}>Validar</Button>}
        </StickyActions>
      )}
      <Modal open={returning} onOpenChange={(o) => !o && !busy && setReturning(false)} title="Devolver ao avaliador"
        description={`${d.answerer_name} poderá ajustar as respostas e enviar de novo.`}
        footer={<><Button variant="secondary" disabled={busy} onClick={() => setReturning(false)}>Cancelar</Button><Button loading={busy} disabled={!note.trim()} onClick={onReturn}>Devolver</Button></>}>
        <TextArea label="O que precisa ser ajustado?" required maxLength={NOTE_MAX} value={note} onChange={(e) => setNote(e.target.value)} />
      </Modal>
    </div>
  );
}
